import { BusinessObjectBase } from './base';
import { tokenSchema } from '@schemas/business-objects/token';
import type { ConditionWatcher } from './condition-watcher';
import type { MessageListener } from './message-listener';
import type { MyUserTask } from './my-user-task';
import type { ProcessInstance } from './process-instance';
import type { ServiceTask } from './service-task';
import type { Tenant } from './tenant';
import type { Timer } from './timer';
import type { TokenHistory } from './token-history';
import type { TokenLink } from './token-link';
import type { UserTask } from './user-task';
import type { Id, Json, TokenStatus } from '../base-types';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessDesign } from './process-design';

export interface TokenData {
  readonly id: Id;
  readonly referenceNumber?: number;
  readonly activityId?: string;
  readonly status?: TokenStatus;
  readonly errorMessage?: string;
  readonly errorCode?: string;
  readonly loopItems?: Json;
  readonly processInstanceId: Id;
  readonly processInstance?: ProcessInstance;
  readonly userTasks?: UserTask[];
  readonly serviceTasks?: ServiceTask[];
  readonly childProcessInstances?: ProcessInstance[];
  readonly tokenHistories?: TokenHistory[];
  readonly downstreamLinks?: TokenLink[];
  readonly upstreamLinks?: TokenLink[];
  readonly timers?: Timer[];
  readonly messageListeners?: MessageListener[];
  readonly myUserTasks?: MyUserTask[];
  readonly conditionWatchers?: ConditionWatcher[];
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface Token extends Readonly<TokenData> {}
type TokenCreateWithChildrenData = Partial<
  Omit<
    TokenData,
    | 'userTasks'
    | 'serviceTasks'
    | 'tokenHistories'
    | 'upstreamLinks'
    | 'timers'
    | 'messageListeners'
    | 'conditionWatchers'
  >
> & {
  userTasks?: Partial<UserTask>[];
  serviceTasks?: Partial<ServiceTask>[];
  tokenHistories?: Partial<TokenHistory>[];
  upstreamLinks?: Partial<TokenLink>[];
  timers?: Partial<Timer>[];
  messageListeners?: Partial<MessageListener>[];
  conditionWatchers?: Partial<ConditionWatcher>[];
};

export class Token extends BusinessObjectBase {
  static readonly entityName = 'Token' as const;
  static readonly plural = 'tokens';
  static schema = tokenSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Token[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Token(data));
  }

  static async findOne(filter?: any): Promise<Token | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Token(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Token> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Token(data);
  }

  static async create(data: Omit<TokenData, 'id'>): Promise<Token> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Token(result);
  }

  static async createMany(data: Omit<TokenData, 'id'>[]): Promise<Token[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Token(item));
  }

  static async createWithChildren(
    data: TokenCreateWithChildrenData,
  ): Promise<Token> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Token(result);
  }

  static async createManyWithChildren(
    data: TokenCreateWithChildrenData[],
  ): Promise<Token[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Token(item));
  }

  static async updateById(id: Id, updates: Partial<TokenData>): Promise<Token> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Token(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async move(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Token.plural}/${(this as any).id}/move`;
    return BusinessObjectBase.post<any>(url, options);
  }
}
